'use client';

import { ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import BevSafeText from '../bev-safe-text';

export type Option = {
	value: string;
	label: string;
	detail?: string;
	locale?: string;
	owsCode?: string;
};

export interface RadioButtonProps {
	option: Option;
	id: string;
	checked?: boolean;
	onChange: (event: React.ChangeEvent<HTMLInputElement>, option: Option) => void;
	internalRadioButtonClassName?: string;
	externalRadioButtonClassName?: string;
	labelContainerClassName?: string;
	labelTextClassName?: string;
	radioButtonLabelClassName?: string;
	radioButtonContainerClassName?: string;
	cirlceContainerClassName?: string;
	externalCircleCheckedClassNames?: string[];
	internalCircleCheckedClassNames?: string[];
	detailContainerClassName?: string;
}

export default function RadioButton({
	option,
	id,
	checked,
	onChange,
	internalRadioButtonClassName,
	externalRadioButtonClassName,
	labelContainerClassName,
	labelTextClassName,
	radioButtonLabelClassName,
	radioButtonContainerClassName,
	cirlceContainerClassName,
	externalCircleCheckedClassNames = [],
	internalCircleCheckedClassNames = [],
	detailContainerClassName,
}: RadioButtonProps): ReactElement {
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onChange(event, option);
	};

	return (
		<div
			className={cn(
				'flex flex-row items-start mb-2.5 lg:mb-0 lg:mr-0 w-full lg:w-auto last:mb-9 lg:last:mb-0',
				radioButtonContainerClassName
			)}
		>
			<label
				className={cn('flex flex-row items-start cursor-pointer', radioButtonLabelClassName)}
				htmlFor={`${id}-${option.value}`}
			>
				<input
					className="peer sr-only"
					type="radio"
					name={id}
					value={option.value}
					checked={checked}
					id={`${id}-${option.value}`}
					onChange={handleChange}
				/>
				<div className={cn('relative w-4 h-4', cirlceContainerClassName)}>
					<span
						className={cn(
							'absolute left-0 top-0 w-full h-full border-2 border-border-subtle rounded-full flex items-center justify-center',
							{
								'bg-button-primary': checked,
								'border-button-primary': checked,
								...externalCircleCheckedClassNames.reduce((acc, className) => {
									acc[className] = checked;
									return acc;
								}, {}),
							},
							externalRadioButtonClassName
						)}
					>
						<span
							className={cn(
								'w-2 h-2 bg-white rounded-full opacity-0',
								{
									'opacity-100': checked,
									...internalCircleCheckedClassNames.reduce((acc, className) => {
										acc[className] = checked;
										return acc;
									}, {}),
								},
								internalRadioButtonClassName
							)}
						></span>
					</span>
				</div>
			</label>
			<div className="flex flex-col">
				{option.label ? (
					<span className={cn('text-subtitle2 w-[200px]', labelContainerClassName)}>
						<span className={cn('text-text-primary', labelTextClassName)}>
							<BevSafeText>{option.label}</BevSafeText>
						</span>
					</span>
				) : null}
				{option.detail && (
					<p className={cn('text-subtitle2 font-bold mt-2 block', detailContainerClassName)}>
						<span className="text-text-primary">{option.detail}</span>
					</p>
				)}
			</div>
		</div>
	);
}
